/**
 * Official mailboxes and communications on the Channels and communications tab of an entity
 * (ALC-4771): the DTE of the RFB and of the CBS, municipal mailboxes, who reads each one, the rule
 * for the days to take notice (ciência) and the communications received. Namespace `officialMailboxes`.
 */
export const officialMailboxesPtBr = {
  title: 'Caixas oficiais',
  description:
    'As caixas onde as autoridades enviam comunicações oficiais (DTE da Receita Federal, DTE da CBS e caixas municipais), quem lê cada uma e o prazo para dar ciência. O ERP ainda não lê as caixas: quem lê registra aqui o que encontrou.',
  tableLabel: 'Caixas oficiais',
  add: 'Registrar caixa',
  loadError: 'Não foi possível carregar as caixas oficiais.',
  empty: {
    title: 'Nenhuma caixa oficial registrada',
    description:
      'Registre o DTE da empresa e quem fica responsável por lê-lo, para que nenhuma comunicação passe do prazo de ciência.',
  },
  readOnly: {
    noTaxpayer:
      'Esta entidade não tem contribuinte federal. Informe a identidade do contribuinte em "Informações básicas" para registrar o DTE. As caixas municipais continuam disponíveis aqui.',
    notHeadOffice:
      'O DTE vale para toda a empresa e só pode ser alterado na matriz. Aqui você vê as caixas e as comunicações.',
    openHeadOffice: 'Abrir a matriz',
    noPermission: 'Somente leitura: você não tem permissão para alterar as caixas oficiais.',
  },
  kind: {
    RFB_DTE: 'DTE da Receita Federal',
    CBS_DTE: 'DTE da CBS',
    MUNICIPAL: 'Caixa municipal',
  },
  scope: {
    TAXPAYER: 'Contribuinte (matriz)',
    ENTITY: 'Estabelecimento',
  },
  inactive: 'Inativa',
  columns: {
    mailbox: 'Caixa',
    reader: 'Quem lê',
    rule: 'Prazo de ciência',
    pending: 'Aguardando ciência',
  },
  reader: {
    none: 'Sem leitor',
  },
  vacancy: {
    UNASSIGNED: 'Sem leitor',
    USER_NOT_FOUND: 'Usuário não encontrado',
    USER_INACTIVE: 'Usuário inativo',
    USER_NO_ENTITY_ACCESS: 'Sem acesso à empresa',
    unavailableSuffix: '(não pode mais atuar)',
  },
  rule: {
    calendar_one: '{{count}} dia corrido',
    calendar_other: '{{count}} dias corridos',
    business_one: '{{count}} dia útil',
    business_other: '{{count}} dias úteis',
    version: 'Versão {{version}}',
    none: 'Nenhuma regra em vigor',
  },
  pending: {
    none: 'Nenhuma',
    count_one: '{{count}} comunicação',
    count_other: '{{count}} comunicações',
    nearest: 'Prazo mais próximo: {{date}}',
  },
  actions: {
    communications: 'Comunicações',
    communicationsLabel: 'Ver as comunicações de {{name}}',
    rule: 'Regra',
    ruleLabel: 'Ver ou alterar a regra de prazo de {{name}}',
    edit: 'Editar',
    editLabel: 'Editar a caixa {{name}}',
  },
  mailboxDialog: {
    createTitle: 'Registrar caixa oficial',
    editTitle: 'Editar caixa oficial',
    description:
      'Indique a caixa, quem fica responsável por lê-la e a regra que dá o prazo de ciência das comunicações.',
    kind: 'Tipo de caixa',
    kindHint: {
      RFB_DTE: 'Vale para todo o contribuinte e é registrada pela matriz.',
      CBS_DTE: 'Vale para todo o contribuinte e é registrada pela matriz.',
      MUNICIPAL: 'Pertence a este estabelecimento.',
    },
    label: 'Nome da caixa',
    labelPlaceholder: 'Por exemplo, Prefeitura de Campinas',
    reader: 'Quem lê a caixa',
    readerHint:
      'Usuário ativo com acesso a esta empresa. Se ele perder o acesso, a caixa fica sem leitor.',
    readerNone: 'Ninguém',
    isActive: 'Caixa em uso',
    isActiveHint: 'Uma caixa inativa não recebe novas comunicações.',
    ruleTitle: 'Regra de prazo de ciência',
    cancel: 'Cancelar',
    create: 'Registrar',
    save: 'Salvar',
    saveError: 'Não foi possível salvar a caixa. Tente novamente.',
  },
  ruleFields: {
    validFrom: 'Vale a partir de',
    validFromPlaceholder: 'dd/mm/aaaa',
    validFromError: 'Informe uma data válida.',
    days: 'Prazo em dias',
    dayType: 'Contagem',
    dayTypeOption: {
      CALENDAR: 'Dias corridos',
      BUSINESS: 'Dias úteis',
    },
    source: 'Fonte',
    sourceHint: 'A norma ou a instrução de onde vem o prazo.',
    sourcePlaceholder: 'Por exemplo, Decreto 70.235/1972, art. 23',
  },
  ruleDialog: {
    title: 'Regra de prazo de ciência',
    description:
      'Cada alteração cria uma nova versão que vale a partir da data informada. As comunicações já registradas mantêm a versão com que o prazo foi calculado.',
    current: 'Em vigor hoje',
    historyTitle: 'Versões',
    historyItem: 'Versão {{version}} · {{days}} · a partir de {{from}}',
    historyItemUntil: 'Versão {{version}} · {{days}} · de {{from}} a {{to}}',
    newTitle: 'Nova versão',
    cancel: 'Fechar',
    submit: 'Registrar nova versão',
    saveError: 'Não foi possível registrar a regra. Tente novamente.',
  },
  communications: {
    title: 'Comunicações de {{name}}',
    description:
      'Registre cada comunicação com a data em que a autoridade a disponibilizou. O ERP calcula o prazo de ciência com a regra da caixa vigente nessa data.',
    tableLabel: 'Comunicações da caixa',
    loadError: 'Não foi possível carregar as comunicações.',
    empty: {
      title: 'Nenhuma comunicação registrada',
      description: 'Registre a primeira comunicação encontrada nesta caixa.',
    },
    record: 'Registrar comunicação',
    back: 'Voltar à lista',
    close: 'Fechar',
    columns: {
      subject: 'Assunto',
      availableOn: 'Disponibilizada em',
      deadline: 'Prazo de ciência',
      status: 'Situação',
    },
    status: {
      RECEIVED: 'Recebida',
      ACKNOWLEDGED: 'Com ciência',
      FORWARDED: 'Encaminhada',
      CLOSED: 'Encerrada',
    },
    deadlineState: {
      OK: 'No prazo',
      NEAR: 'Perto do prazo',
      TACIT_ELAPSED: 'Prazo vencido: ciência tácita',
    },
    daysLeft_zero: 'Vence hoje',
    daysLeft_one: 'Falta {{count}} dia',
    daysLeft_other: 'Faltam {{count}} dias',
    daysAgo_one: 'Venceu há {{count}} dia',
    daysAgo_other: 'Venceu há {{count}} dias',
    deadlineVersion: 'Regra versão {{version}}: {{days}}. {{source}}',
    reference: 'Nº {{reference}}',
    actions: {
      acknowledge: 'Dar ciência',
      forward: 'Encaminhar',
      closeCommunication: 'Encerrar',
      label: '{{action}}: {{subject}}',
    },
    history: {
      received: 'Registrada por {{name}} em {{date}}',
      acknowledgedExpress: 'Ciência expressa em {{date}}, por {{name}}',
      acknowledgedTacit:
        'Ciência tácita: o prazo terminou em {{effective}} e a ciência foi registrada em {{date}}, por {{name}}',
      forwarded: 'Encaminhada a {{to}} por {{name}} em {{date}}',
      forwardedNote: 'Orientação: {{note}}',
      closed: 'Encerrada por {{name}} em {{date}}: {{outcome}}',
      closedNote: 'Observação: {{note}}',
      unknownUser: 'usuário removido',
    },
    outcome: {
      NO_ACTION_REQUIRED: 'Nenhuma providência necessária',
      RESPONDED: 'Respondida',
      REGULARIZED: 'Regularizada',
      CONTESTED: 'Contestada',
      OTHER: 'Outro desfecho',
    },
    form: {
      cancel: 'Cancelar',
      saveError: 'Não foi possível salvar. Tente novamente.',
      recordTitle: 'Registrar comunicação',
      subject: 'Assunto',
      referenceNumber: 'Número ou protocolo',
      availableOn: 'Disponibilizada em',
      availableOnHint: 'O prazo de ciência conta a partir do dia seguinte.',
      dateError: 'Informe uma data válida.',
      recordSubmit: 'Registrar',
      acknowledgeTitle: 'Dar ciência',
      acknowledgeDescription:
        'Informe o dia em que a comunicação foi lida. Depois do prazo, a ciência é tácita e vale desde o último dia do prazo.',
      acknowledgedOn: 'Ciência em',
      acknowledgeSubmit: 'Registrar ciência',
      forwardTitle: 'Encaminhar',
      forwardDescription: 'Escolha quem vai tratar a comunicação.',
      forwardTo: 'Encaminhar a',
      forwardNone: 'Escolha uma pessoa',
      forwardNote: 'Orientação',
      forwardSubmit: 'Encaminhar',
      closeTitle: 'Encerrar comunicação',
      closeDescription: 'Registre como a comunicação foi tratada.',
      outcome: 'Desfecho',
      outcomeNote: 'Observação',
      closeSubmit: 'Encerrar',
    },
  },
  errors: {
    OBLIGATION_DATE_INVALID: 'Informe uma data válida.',
    OBLIGATION_RETRY: 'Os dados mudaram durante o salvamento. Atualize a página e tente novamente.',
    OBLIGATION_RESPONSIBLE_NOT_ELIGIBLE: 'Escolha um usuário ativo com acesso a esta empresa.',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY: 'O DTE só pode ser alterado na matriz do contribuinte.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'Informe a identidade do contribuinte em "Informações básicas" para registrar o DTE.',
    MAILBOX_NOT_FOUND: 'Não encontramos essa caixa. Atualize a página e tente novamente.',
    MAILBOX_ALREADY_EXISTS:
      'Já existe uma caixa deste tipo (ou com este nome) para esta empresa. Edite a que já existe.',
    MAILBOX_LABEL_REQUIRED: 'Informe o nome da caixa municipal, por exemplo a prefeitura.',
    MAILBOX_LABEL_INVALID:
      'O nome da caixa passa de 120 caracteres, ou é um DTE, que não pode ser renomeado.',
    MAILBOX_INACTIVE: 'A caixa está inativa. Reative a caixa para registrar comunicações.',
    MAILBOX_RULE_INVALID:
      'A regra precisa de 1 a 365 dias, do tipo de contagem e da fonte do prazo.',
    MAILBOX_RULE_DATE_INVALID:
      'A nova versão precisa começar depois da versão em vigor. Versões anteriores não mudam.',
    MAILBOX_RULE_OVERLAP:
      'A versão se sobrepõe a outra versão da caixa. Escolha uma data de início que não coincida com a de outra versão.',
    MAILBOX_RULE_PERIOD_LOCKED:
      'A regra alcança um período já fechado, que não pode ser alterado. Escolha uma data de início posterior.',
    MAILBOX_RULE_NOT_IN_FORCE:
      'Nenhuma regra de prazo valia na data de disponibilização. Registre uma regra que comece até essa data.',
    COMMUNICATION_NOT_FOUND:
      'Não encontramos essa comunicação. Atualize a página e tente novamente.',
    COMMUNICATION_DETAILS_REQUIRED: 'Informe o assunto e a data de disponibilização.',
    COMMUNICATION_DATE_INVALID:
      'Informe uma data válida, que não seja futura nem anterior à disponibilização.',
    COMMUNICATION_STATUS_INVALID:
      'Esta etapa não é possível no estado atual da comunicação. Atualize a página e tente novamente.',
    COMMUNICATION_OUTCOME_REQUIRED: 'Para encerrar, informe o desfecho e uma observação.',
  },
};

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const officialMailboxesEn: Shape<typeof officialMailboxesPtBr> = {
  title: 'Official mailboxes',
  description:
    'The mailboxes where authorities post official communications (the RFB DTE, the CBS DTE and municipal mailboxes), who reads each one and the deadline to take notice. The ERP does not read the mailboxes yet: whoever reads records here what they found.',
  tableLabel: 'Official mailboxes',
  add: 'Register mailbox',
  loadError: 'Could not load the official mailboxes.',
  empty: {
    title: 'No official mailbox registered',
    description:
      'Register the company DTE and who is responsible for reading it, so no communication passes its deadline to take notice.',
  },
  readOnly: {
    noTaxpayer:
      'This entity has no federal Taxpayer. Set the Taxpayer identity in "Basic information" to register the DTE. Municipal mailboxes are still available here.',
    notHeadOffice:
      'The DTE applies to the whole company and can only be changed at the head office. Here you can see the mailboxes and communications.',
    openHeadOffice: 'Open the head office',
    noPermission: 'Read only: you do not have permission to change the official mailboxes.',
  },
  kind: {
    RFB_DTE: 'Federal Revenue DTE',
    CBS_DTE: 'CBS DTE',
    MUNICIPAL: 'Municipal mailbox',
  },
  scope: {
    TAXPAYER: 'Taxpayer (head office)',
    ENTITY: 'Establishment',
  },
  inactive: 'Inactive',
  columns: {
    mailbox: 'Mailbox',
    reader: 'Who reads it',
    rule: 'Deadline to take notice',
    pending: 'Waiting for notice',
  },
  reader: {
    none: 'No reader',
  },
  vacancy: {
    UNASSIGNED: 'No reader',
    USER_NOT_FOUND: 'User not found',
    USER_INACTIVE: 'User inactive',
    USER_NO_ENTITY_ACCESS: 'No access to the company',
    unavailableSuffix: '(can no longer act)',
  },
  rule: {
    calendar_one: '{{count}} calendar day',
    calendar_other: '{{count}} calendar days',
    business_one: '{{count}} business day',
    business_other: '{{count}} business days',
    version: 'Version {{version}}',
    none: 'No rule in force',
  },
  pending: {
    none: 'None',
    count_one: '{{count}} communication',
    count_other: '{{count}} communications',
    nearest: 'Nearest deadline: {{date}}',
  },
  actions: {
    communications: 'Communications',
    communicationsLabel: 'See the communications of {{name}}',
    rule: 'Rule',
    ruleLabel: 'See or change the deadline rule of {{name}}',
    edit: 'Edit',
    editLabel: 'Edit the mailbox {{name}}',
  },
  mailboxDialog: {
    createTitle: 'Register official mailbox',
    editTitle: 'Edit official mailbox',
    description:
      'Choose the mailbox, who is responsible for reading it, and the rule that gives the deadline to take notice of its communications.',
    kind: 'Mailbox type',
    kindHint: {
      RFB_DTE: 'Applies to the whole Taxpayer and is registered at the head office.',
      CBS_DTE: 'Applies to the whole Taxpayer and is registered at the head office.',
      MUNICIPAL: 'Belongs to this establishment.',
    },
    label: 'Mailbox name',
    labelPlaceholder: 'For example, Campinas city hall',
    reader: 'Who reads the mailbox',
    readerHint:
      'An active user with access to this company. If they lose access, the mailbox is left without a reader.',
    readerNone: 'Nobody',
    isActive: 'Mailbox in use',
    isActiveHint: 'An inactive mailbox receives no new communications.',
    ruleTitle: 'Deadline rule to take notice',
    cancel: 'Cancel',
    create: 'Register',
    save: 'Save',
    saveError: 'Could not save the mailbox. Try again.',
  },
  ruleFields: {
    validFrom: 'Valid from',
    validFromPlaceholder: 'dd/mm/yyyy',
    validFromError: 'Enter a valid date.',
    days: 'Deadline in days',
    dayType: 'Counting',
    dayTypeOption: {
      CALENDAR: 'Calendar days',
      BUSINESS: 'Business days',
    },
    source: 'Source',
    sourceHint: 'The rule or instruction the deadline comes from.',
    sourcePlaceholder: 'For example, Decree 70,235/1972, art. 23',
  },
  ruleDialog: {
    title: 'Deadline rule to take notice',
    description:
      'Each change creates a new version that applies from the date you enter. Communications already recorded keep the version their deadline was calculated with.',
    current: 'In force today',
    historyTitle: 'Versions',
    historyItem: 'Version {{version}} · {{days}} · from {{from}}',
    historyItemUntil: 'Version {{version}} · {{days}} · from {{from}} to {{to}}',
    newTitle: 'New version',
    cancel: 'Close',
    submit: 'Register new version',
    saveError: 'Could not register the rule. Try again.',
  },
  communications: {
    title: 'Communications of {{name}}',
    description:
      'Record each communication with the day the authority made it available. The ERP calculates the deadline to take notice with the mailbox rule in force on that day.',
    tableLabel: 'Communications of the mailbox',
    loadError: 'Could not load the communications.',
    empty: {
      title: 'No communication recorded',
      description: 'Record the first communication found in this mailbox.',
    },
    record: 'Record communication',
    back: 'Back to the list',
    close: 'Close',
    columns: {
      subject: 'Subject',
      availableOn: 'Made available on',
      deadline: 'Deadline to take notice',
      status: 'Status',
    },
    status: {
      RECEIVED: 'Received',
      ACKNOWLEDGED: 'Notice taken',
      FORWARDED: 'Forwarded',
      CLOSED: 'Closed',
    },
    deadlineState: {
      OK: 'Within the deadline',
      NEAR: 'Near the deadline',
      TACIT_ELAPSED: 'Deadline passed: tacit notice',
    },
    daysLeft_zero: 'Due today',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    daysAgo_one: 'Passed {{count}} day ago',
    daysAgo_other: 'Passed {{count}} days ago',
    deadlineVersion: 'Rule version {{version}}: {{days}}. {{source}}',
    reference: 'No. {{reference}}',
    actions: {
      acknowledge: 'Take notice',
      forward: 'Forward',
      closeCommunication: 'Close',
      label: '{{action}}: {{subject}}',
    },
    history: {
      received: 'Recorded by {{name}} on {{date}}',
      acknowledgedExpress: 'Express notice on {{date}}, by {{name}}',
      acknowledgedTacit:
        'Tacit notice: the deadline ended on {{effective}} and notice was recorded on {{date}}, by {{name}}',
      forwarded: 'Forwarded to {{to}} by {{name}} on {{date}}',
      forwardedNote: 'Instruction: {{note}}',
      closed: 'Closed by {{name}} on {{date}}: {{outcome}}',
      closedNote: 'Note: {{note}}',
      unknownUser: 'removed user',
    },
    outcome: {
      NO_ACTION_REQUIRED: 'No action required',
      RESPONDED: 'Responded',
      REGULARIZED: 'Regularized',
      CONTESTED: 'Contested',
      OTHER: 'Other outcome',
    },
    form: {
      cancel: 'Cancel',
      saveError: 'Could not save. Try again.',
      recordTitle: 'Record communication',
      subject: 'Subject',
      referenceNumber: 'Number or protocol',
      availableOn: 'Made available on',
      availableOnHint: 'The deadline to take notice counts from the next day.',
      dateError: 'Enter a valid date.',
      recordSubmit: 'Record',
      acknowledgeTitle: 'Take notice',
      acknowledgeDescription:
        'Enter the day the communication was read. After the deadline, notice is tacit and applies from the last day of the deadline.',
      acknowledgedOn: 'Notice on',
      acknowledgeSubmit: 'Record notice',
      forwardTitle: 'Forward',
      forwardDescription: 'Choose who will handle the communication.',
      forwardTo: 'Forward to',
      forwardNone: 'Choose a person',
      forwardNote: 'Instruction',
      forwardSubmit: 'Forward',
      closeTitle: 'Close communication',
      closeDescription: 'Record how the communication was handled.',
      outcome: 'Outcome',
      outcomeNote: 'Note',
      closeSubmit: 'Close',
    },
  },
  errors: {
    OBLIGATION_DATE_INVALID: 'Enter a valid date.',
    OBLIGATION_RETRY: 'The data changed while saving. Refresh the page and try again.',
    OBLIGATION_RESPONSIBLE_NOT_ELIGIBLE: 'Choose an active user with access to this company.',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'The DTE can only be changed at the Taxpayer head office.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'Set the Taxpayer identity in "Basic information" to register the DTE.',
    MAILBOX_NOT_FOUND: 'We could not find this mailbox. Refresh the page and try again.',
    MAILBOX_ALREADY_EXISTS:
      'This company already has a mailbox of this kind (or with this name). Edit the existing one.',
    MAILBOX_LABEL_REQUIRED: 'Enter the name of the municipal mailbox, for example the city hall.',
    MAILBOX_LABEL_INVALID:
      'The mailbox name is over 120 characters, or it is a DTE, which cannot be renamed.',
    MAILBOX_INACTIVE: 'The mailbox is inactive. Reactivate it to record communications.',
    MAILBOX_RULE_INVALID: 'The rule needs 1 to 365 days, the kind of counting and the source.',
    MAILBOX_RULE_DATE_INVALID:
      'The new version must start after the version in force. Earlier versions do not change.',
    MAILBOX_RULE_OVERLAP:
      'The version overlaps another version of the mailbox. Choose a start date that does not match another version.',
    MAILBOX_RULE_PERIOD_LOCKED:
      'The rule covers a period that is already closed and cannot be changed. Choose a later start date.',
    MAILBOX_RULE_NOT_IN_FORCE:
      'No deadline rule was in force on the day the communication was made available. Register a rule that starts by that date.',
    COMMUNICATION_NOT_FOUND:
      'We could not find this communication. Refresh the page and try again.',
    COMMUNICATION_DETAILS_REQUIRED: 'Enter the subject and the day it was made available.',
    COMMUNICATION_DATE_INVALID:
      'Enter a valid date that is not in the future and not before the communication was made available.',
    COMMUNICATION_STATUS_INVALID:
      'This step is not possible in the current status of the communication. Refresh the page and try again.',
    COMMUNICATION_OUTCOME_REQUIRED: 'To close, enter the outcome and a note.',
  },
};
